// Modal dialog + the confirm dialog every destructive action goes through.
//
//   <ConfirmDialog
//     open={open}
//     onClose={() => setOpen(false)}
//     title="Delete tenant"
//     description="This removes every season, game and user membership."
//     confirmLabel="Delete tenant"
//     tone="danger"
//     typeToConfirm={tenant.subdomain}           // must be typed exactly
//     reason={{ label: 'Reason', minLength: 5 }}  // optional required reason
//     onConfirm={async ({ reason }) => api.tenants.suspend(id, reason!)}
//   />
//
// The dialog stays open while onConfirm runs, shows its error inline if it
// throws, and closes only on success.

import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { errorMessage } from '@/api/client'
import { cn } from '@/lib/format'
import { Button } from './Button'
import { FormField, TextInput, Textarea } from './Field'
import { InlineAlert } from './States'
import { XIcon } from './icons'

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export interface DialogProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  /** Block Escape / backdrop / close button (e.g. while saving). */
  dismissible?: boolean
  size?: 'sm' | 'md' | 'lg'
  /** Element to focus first (defaults to the first field, else the first button). */
  initialFocusRef?: RefObject<HTMLElement>
}

export function Dialog({ open, onClose, title, description, children, footer, dismissible = true, size = 'sm', initialFocusRef }: DialogProps) {
  const titleId = useId()
  const descId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const dismissibleRef = useRef(dismissible)
  dismissibleRef.current = dismissible

  useEffect(() => {
    if (!open) return
    const previouslyFocused = document.activeElement as HTMLElement | null
    const panel = panelRef.current
    const first =
      initialFocusRef?.current ??
      panel?.querySelector<HTMLElement>('input:not([disabled]), textarea:not([disabled]), select:not([disabled])') ??
      panel?.querySelector<HTMLElement>(FOCUSABLE)
    first?.focus()

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissibleRef.current) {
        e.stopPropagation()
        onCloseRef.current()
      }
      if (e.key === 'Tab' && panel) {
        const nodes = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE))
        if (nodes.length === 0) return
        const firstNode = nodes[0]
        const lastNode = nodes[nodes.length - 1]
        if (e.shiftKey && document.activeElement === firstNode) {
          e.preventDefault()
          lastNode.focus()
        } else if (!e.shiftKey && document.activeElement === lastNode) {
          e.preventDefault()
          firstNode.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
      previouslyFocused?.focus?.()
    }
  }, [open, initialFocusRef])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6">
      <div aria-hidden className="absolute inset-0 animate-fade-in bg-black/60" onClick={() => dismissible && onClose()} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        className={cn(
          'relative w-full animate-fade-in rounded-t-xl border border-line bg-panel shadow-dialog sm:rounded-xl',
          'max-h-[92vh] overflow-y-auto',
          size === 'sm' ? 'sm:max-w-md' : size === 'md' ? 'sm:max-w-lg' : 'sm:max-w-2xl',
        )}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex items-start justify-between gap-4 px-5 pb-2 pt-5">
          <h2 id={titleId} className="text-2xl font-extrabold leading-none text-ink">
            {title}
          </h2>
          {dismissible ? (
            <button type="button" onClick={onClose} aria-label="Close" className="-m-1.5 rounded p-1.5 text-muted hover:bg-hover hover:text-ink">
              <XIcon size={18} />
            </button>
          ) : null}
        </div>
        {description ? (
          <div id={descId} className="px-5 text-[13.5px] text-muted">
            {description}
          </div>
        ) : null}
        {children ? <div className="px-5 pt-4">{children}</div> : null}
        {footer ? <div className="mt-5 flex flex-col-reverse gap-2 border-t border-line px-5 py-4 sm:flex-row sm:justify-end">{footer}</div> : <div className="h-5" />}
      </div>
    </div>,
    document.body,
  )
}

export interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (input: { reason?: string }) => Promise<unknown> | unknown
  title: ReactNode
  description?: ReactNode
  confirmLabel: string
  cancelLabel?: string
  tone?: 'danger' | 'primary'
  /** The user must type this exact text (e.g. the subdomain) to enable Confirm. */
  typeToConfirm?: string
  /** Require a written reason (e.g. for Suspend). */
  reason?: { label?: string; placeholder?: string; minLength?: number }
  children?: ReactNode
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'danger',
  typeToConfirm,
  reason,
  children,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('')
  const [reasonText, setReasonText] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fresh state every time it opens.
  useEffect(() => {
    if (open) {
      setTyped('')
      setReasonText('')
      setError(null)
      setPending(false)
    }
  }, [open])

  const minReason = reason?.minLength ?? 1
  const typedOk = !typeToConfirm || typed.trim() === typeToConfirm
  const reasonOk = !reason || reasonText.trim().length >= minReason
  const canConfirm = typedOk && reasonOk && !pending

  const submit = async () => {
    if (!canConfirm) return
    setPending(true)
    setError(null)
    try {
      await onConfirm({ reason: reason ? reasonText.trim() : undefined })
      setPending(false)
      onClose()
    } catch (err) {
      setPending(false)
      setError(errorMessage(err))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      dismissible={!pending}
      footer={
        <>
          <Button onClick={onClose} disabled={pending}>
            {cancelLabel}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={submit} disabled={!(typedOk && reasonOk)} loading={pending}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        {children}
        {reason ? (
          <FormField
            label={reason.label ?? 'Reason'}
            required
            hint={minReason > 1 ? `At least ${minReason} characters. Saved in the audit log.` : 'Saved in the audit log.'}
          >
            {({ id, describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                value={reasonText}
                placeholder={reason.placeholder}
                onChange={(e) => setReasonText(e.target.value)}
                disabled={pending}
              />
            )}
          </FormField>
        ) : null}
        {typeToConfirm ? (
          <FormField
            label={
              <>
                Type <span className="font-mono text-accent">{typeToConfirm}</span> to confirm
              </>
            }
          >
            {({ id }) => (
              <TextInput
                id={id}
                mono
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                disabled={pending}
              />
            )}
          </FormField>
        ) : null}
        {error ? <InlineAlert tone="bad">{error}</InlineAlert> : null}
      </form>
    </Dialog>
  )
}
